import { useState } from "react";
import { toast } from "sonner";
import { User, Phone, Mail, MapPin, Pencil, X, Check } from "lucide-react";
import api from "@/lib/api";

const FIELDS = [
  ["prenom", "Prénom", "text"],
  ["nom", "Nom", "text"],
  ["telephone", "Téléphone", "tel"],
  ["email", "Email", "email"],
  ["adresse", "Adresse du chantier", "text"],
  ["code_postal", "Code postal", "text"],
  ["ville", "Ville", "text"],
];

export const ClientCard = ({ project, onSaved }) => {
  const c = project.contact || {};
  const complete = !!c.nom;
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(c);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!(form.nom || "").trim()) { toast.error("Le nom du client est obligatoire"); return; }
    setBusy(true);
    try {
      const { data } = await api.patch(`/chantiers/${project.id}`, { contact: form });
      toast.success("Fiche client enregistrée");
      setOpen(false);
      onSaved?.(data);
    } catch { toast.error("Enregistrement impossible"); }
    finally { setBusy(false); }
  };

  if (open) {
    return (
      <div data-testid="client-card-form" className="bg-white border border-black/10 rounded-rama-md p-5 md:p-6">
        <div className="flex items-center justify-between mb-5">
          <div className="font-display text-xl text-anthracite">Fiche client</div>
          <button data-testid="client-card-cancel" onClick={() => { setForm(c); setOpen(false); }} className="text-ink-soft hover:text-anthracite"><X size={18} /></button>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          {FIELDS.map(([k, l, t]) => (
            <label key={k} className={`text-sm text-ink-soft ${k === "adresse" ? "sm:col-span-2" : ""}`}>
              {l}{k === "nom" && " *"}
              <input type={t} data-testid={`client-${k}`} value={form[k] || ""}
                onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                className="block w-full mt-1 border-b border-black/25 py-2 bg-transparent focus:border-bordeaux-700 outline-none text-anthracite" />
            </label>
          ))}
        </div>
        <button data-testid="client-card-save" onClick={save} disabled={busy} className="btn-primary mt-6 disabled:opacity-50">
          {busy ? "Enregistrement…" : "Enregistrer"} {!busy && <Check size={16} />}
        </button>
      </div>
    );
  }

  return (
    <div data-testid="client-card" className="bg-white border border-black/10 rounded-rama-md p-5 flex flex-wrap items-center gap-x-8 gap-y-3">
      {complete ? (
        <>
          <span className="inline-flex items-center gap-2 text-sm text-anthracite"><User size={14} className="text-bordeaux-700" />{`${c.prenom || ""} ${c.nom}`.trim()}</span>
          {c.telephone && <a href={`tel:${c.telephone}`} data-testid="client-phone" className="inline-flex items-center gap-2 text-sm text-anthracite hover:text-bordeaux-700"><Phone size={14} className="text-bordeaux-700" />{c.telephone}</a>}
          {c.email && <a href={`mailto:${c.email}`} className="inline-flex items-center gap-2 text-sm text-anthracite hover:text-bordeaux-700"><Mail size={14} className="text-bordeaux-700" />{c.email}</a>}
          {(c.adresse || c.ville) && <span className="inline-flex items-center gap-2 text-sm text-ink-soft"><MapPin size={14} className="text-bordeaux-700" />{[c.adresse, c.code_postal, c.ville].filter(Boolean).join(" · ")}</span>}
        </>
      ) : (
        <span className="text-sm text-ink-soft">Aucune coordonnée renseignée pour ce prospect.</span>
      )}
      <button data-testid="client-card-edit" onClick={() => { setForm(c); setOpen(true); }} className="btn-ghost ml-auto text-sm">
        <Pencil size={14} /> {complete ? "Modifier la fiche" : "Compléter la fiche client"}
      </button>
    </div>
  );
};

export default ClientCard;
